import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Link as LinkIcon,
  Copy,
  Trash2,
  Plus,
  ExternalLink,
  TrendingUp,
  Calendar,
  Users as UsersIcon,
  Loader2,
  Check,
} from 'lucide-react';
import {
  Card,
  Button,
  Modal,
  ModalFooter,
  Input,
  Badge,
} from '@azyntor-labs/nexus-ui';
import { enrollmentLinksApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { format } from 'date-fns';

interface Props {
  examId: string;
}

export const EnrollmentLinkManager = ({ examId }: Props) => {
  const queryClient = useQueryClient();
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [maxUses, setMaxUses] = useState<number | ''>('');
  const [expiresAt, setExpiresAt] = useState('');
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // Fetch existing links
  const { data: linksResponse, isLoading } = useQuery({
    queryKey: ['enrollment-links', examId],
    queryFn: () => enrollmentLinksApi.list(examId),
    enabled: !!examId,
  });

  const links = linksResponse?.data?.data || [];

  // Generate link mutation
  const generateMutation = useMutation({
    mutationFn: (data: any) => enrollmentLinksApi.generate(examId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['enrollment-links', examId] });
      toast.success('Enrollment link generated successfully');
      setIsGenerateModalOpen(false);
      setLinkName('');
      setMaxUses('');
      setExpiresAt('');

      // Auto-copy the generated link
      const linkUrl = response.data?.data?.link_url;
      if (linkUrl) {
        navigator.clipboard.writeText(linkUrl);
        toast.success('Link copied to clipboard!');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to generate link');
    },
  });

  // Deactivate link mutation
  const deactivateMutation = useMutation({
    mutationFn: (linkId: string) => enrollmentLinksApi.deactivate(linkId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment-links', examId] });
      toast.success('Link deactivated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to deactivate link');
    },
  });

  const handleGenerate = () => {
    if (!linkName.trim()) {
      toast.error('Please enter a link name');
      return;
    }

    const data: any = {
      name: linkName.trim(),
    };

    if (maxUses && Number(maxUses) > 0) {
      data.max_uses = Number(maxUses);
    }

    if (expiresAt) {
      data.expires_at = new Date(expiresAt).toISOString();
    }

    generateMutation.mutate(data);
  };

  const handleCopyLink = (linkUrl: string, linkId: string) => {
    navigator.clipboard.writeText(linkUrl);
    setCopiedLinkId(linkId);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopiedLinkId(null), 2000);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Enrollment Links</h3>
          <p className="text-sm text-gray-400">Generate shareable links for campaign tracking</p>
        </div>
        <Button
          onClick={() => setIsGenerateModalOpen(true)}
          leftIcon={<Plus className="w-4 h-4" />}
          className="bg-teal-500 hover:bg-teal-600 text-white"
        >
          Generate Link
        </Button>
      </div>

      {/* Links List */}
      {isLoading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 mx-auto text-teal-400 animate-spin mb-3" />
          <p className="text-gray-400">Loading links...</p>
        </div>
      ) : links.length === 0 ? (
        <Card className="bg-[#12121a]/80 border-white/[0.06] p-8 text-center">
          <LinkIcon className="w-12 h-12 mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400 mb-4">No enrollment links created yet</p>
          <Button
            onClick={() => setIsGenerateModalOpen(true)}
            size="sm"
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            Create First Link
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {links.map((link: any) => (
            <Card key={link.id} className="bg-[#12121a]/80 border-white/[0.06] p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-medium text-white">{link.name}</h4>
                    {link.is_active ? (
                      <Badge variant="success" className="text-xs">Active</Badge>
                    ) : (
                      <Badge variant="danger" className="text-xs">Inactive</Badge>
                    )}
                  </div>

                  {/* Link URL */}
                  <div className="flex items-center gap-2 mb-3">
                    <code className="text-sm text-teal-400 bg-[#0d0d12] px-3 py-1.5 rounded border border-white/[0.06] flex-1">
                      {link.link_url}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyLink(link.link_url, link.id)}
                      leftIcon={copiedLinkId === link.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      className={copiedLinkId === link.id ? 'text-emerald-400' : 'text-gray-400 hover:text-teal-400'}
                    >
                      {copiedLinkId === link.id ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-400">
                        Clicks: <span className="text-white font-medium">{link.stats?.total_clicks || 0}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <UsersIcon className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-400">
                        Enrollments: <span className="text-emerald-400 font-medium">{link.stats?.enrollments || 0}</span>
                      </span>
                    </div>
                    {link.stats && link.stats.total_clicks > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">
                          Conversion: <span className="text-teal-400 font-medium">{link.stats.conversion_rate?.toFixed(1)}%</span>
                        </span>
                      </div>
                    )}
                    {link.max_uses && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">
                          Uses: <span className="text-white font-medium">{link.current_uses}/{link.max_uses}</span>
                        </span>
                      </div>
                    )}
                    {link.expires_at && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-400">
                          Expires: {format(new Date(link.expires_at), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(link.link_url, '_blank')}
                    leftIcon={<ExternalLink className="w-3 h-3" />}
                    className="text-cyan-400 hover:text-cyan-300"
                  >
                    Preview
                  </Button>
                  {link.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deactivateMutation.mutate(link.id)}
                      disabled={deactivateMutation.isPending}
                      leftIcon={<Trash2 className="w-3 h-3" />}
                      className="text-red-400 hover:text-red-300"
                    >
                      Deactivate
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Generate Link Modal */}
      <Modal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        title="Generate Enrollment Link"
        size="lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Create a shareable link for students to self-enroll. Perfect for LinkedIn, Google Ads, or email campaigns.
          </p>

          <Input
            label="Link Name"
            placeholder="e.g., LinkedIn Campaign - CEH Winter 2025"
            value={linkName}
            onChange={(e) => setLinkName(e.target.value)}
            className="bg-[#0d0d12] border-white/[0.06] text-white"
            required
          />

          <Input
            label="Max Uses (Optional)"
            type="number"
            placeholder="Leave empty for unlimited"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value ? Number(e.target.value) : '')}
            className="bg-[#0d0d12] border-white/[0.06] text-white"
          />

          <Input
            label="Expires At (Optional)"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="bg-[#0d0d12] border-white/[0.06] text-white"
          />

          <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg p-3">
            <p className="text-sm text-teal-400">
              <strong>Tip:</strong> Add UTM parameters to track campaign performance:
              <code className="block mt-1 text-xs text-gray-300">
                ?utm_source=linkedin&utm_campaign=ceh_winter_2025
              </code>
            </p>
          </div>
        </div>

        <ModalFooter>
          <Button
            variant="ghost"
            onClick={() => setIsGenerateModalOpen(false)}
            className="text-gray-400"
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!linkName.trim() || generateMutation.isPending}
            leftIcon={generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            {generateMutation.isPending ? 'Generating...' : 'Generate Link'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
